import { memo } from "react";

import { Share2, Check } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "@/components/ui/sonner";

interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  tier: string;
  points_reward: number;
  unlocked_at?: string;
}

interface AchievementBadgeProps {
  achievement: Achievement;
  size?: "sm" | "md" | "lg";
  showShareButton?: boolean;
}

const TIER_COLORS = {
  bronze: "from-orange-700 to-orange-500",
  silver: "from-gray-400 to-gray-300",
  gold: "from-yellow-500 to-yellow-400",
  platinum: "from-cyan-400 to-cyan-300",
  diamond: "from-blue-400 to-purple-400",
};

const TIER_GLOW = {
  bronze: "shadow-orange-500/50",
  silver: "shadow-gray-400/50",
  gold: "shadow-yellow-500/50",
  platinum: "shadow-cyan-400/50",
  diamond: "shadow-blue-400/50",
};

const AchievementBadgeComponent = ({
  achievement,
  size = "md",
  showShareButton = true,
}: AchievementBadgeProps) => {
  const sizeClasses = {
    sm: "w-20 h-20 text-2xl",
    md: "w-28 h-28 text-4xl",
    lg: "w-36 h-36 text-5xl",
  };

  const handleShare = async () => {
    const shareText = `🏆 I unlocked the "${achievement.name}" achievement! ${achievement.icon}\n\n${achievement.description}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Achievement Unlocked: ${achievement.name}`,
          text: shareText,
        });
        toast.success("Achievement shared!");
      } catch (error) {
        if ((error as Error).name !== "AbortError") {
          fallbackCopy(shareText);
        }
      }
    } else {
      fallbackCopy(shareText);
    }
  };

  const fallbackCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Achievement copied to clipboard!");
  };

  const tierColor = TIER_COLORS[achievement.tier as keyof typeof TIER_COLORS];
  const tierGlow = TIER_GLOW[achievement.tier as keyof typeof TIER_GLOW];

  return (
    <div className="group relative">
      <Card
        className={`
          relative overflow-hidden
          bg-linear-to-br ${tierColor}
          border-2 border-background
          ${tierGlow} shadow-lg
          hover:scale-105 hover:shadow-xl
          transition-all duration-300
          ${sizeClasses[size]}
          flex items-center justify-center
          animate-scale-in
        `}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.8),transparent_50%)]" />
        </div>

        {/* Icon */}
        <div className="relative z-10 flex flex-col items-center justify-center">
          <span className="drop-shadow-lg">{achievement.icon}</span>
        </div>

        {/* Tier Badge */}
        <Badge
          variant="secondary"
          className="absolute top-1 right-1 text-xs capitalize bg-background/90"
        >
          {achievement.tier}
        </Badge>

        {/* Checkmark for unlocked */}
        {achievement.unlocked_at && (
          <div className="absolute bottom-1 right-1 w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
            <Check className="h-4 w-4 text-white" />
          </div>
        )}
      </Card>

      {/* Hover Info */}
      <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-max max-w-[200px] opacity-0 group-hover:opacity-100 transition-opacity z-20 pointer-events-none">
        <Card className="p-2 bg-popover border shadow-lg">
          <p className="text-xs font-semibold text-center">{achievement.name}</p>
          <p className="text-xs text-muted-foreground text-center mt-1">
            {achievement.description}
          </p>
          <div className="flex items-center justify-center gap-2 mt-2">
            <Badge variant="outline" className="text-xs">
              +{achievement.points_reward} points
            </Badge>
            {achievement.unlocked_at && (
              <span className="text-xs text-muted-foreground">
                {new Date(achievement.unlocked_at).toLocaleDateString()}
              </span>
            )}
          </div>
        </Card>
      </div>

      {/* Share Button */}
      {showShareButton && achievement.unlocked_at && (
        <Button
          variant="secondary"
          size="sm"
          className="absolute -bottom-8 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={handleShare}
        >
          <Share2 className="h-3 w-3 mr-1" />
          Share
        </Button>
      )}
    </div>
  );
};

export const AchievementBadge = memo(AchievementBadgeComponent);
